// Colour helpers + the editor's palettes (picker maths, recent-colour
// persistence, markup/note swatch sets, inspector outline colours).

// ─────────────────────────── colour picker ───────────────────────────

export function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const n = m ? parseInt(m[1]!, 16) : 0;
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d > 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  const to = (u: number) =>
    Math.round((u + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

export const PICKER_SWATCHES = [
  "#000000", "#495057", "#868e96", "#ced4da", "#ffffff", "#e03131",
  "#f08c00", "#fab005", "#ffe066", "#2f9e44", "#12b886", "#b2f2bb",
  "#1971c2", "#4263eb", "#a5d8ff", "#7048e8", "#c2255c", "#ffc9c9",
];

export const RECENT_COLORS_KEY = "xtract-pdf-editor-recent-colors";

export function loadRecentColors(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_COLORS_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((c) => typeof c === "string").slice(0, 8) : [];
  } catch {
    return [];
  }
}

export function pushRecentColor(hex: string): void {
  try {
    const next = [hex, ...loadRecentColors().filter((c) => c !== hex)].slice(0, 8);
    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(next));
  } catch {
    /* private mode */
  }
}

export const HIGHLIGHT_COLORS = [
  "#ffe066", // yellow
  "#b2f2bb", // green
  "#a5d8ff", // blue
  "#fcc2d7", // pink
  "#ffd8a8", // orange
];

// Saturated set for underline / strikeout / squiggly, where the pastel
// highlight palette would be too faint at 1–2 pt line widths.
export const MARKUP_COLORS = [
  "#e03131", // red
  "#2f9e44", // green
  "#1971c2", // blue
  "#f08c00", // orange
  "#343a40", // near-black
];

export const NOTE_COLORS = [
  "#fab005", // yellow
  "#ff922b", // orange
  "#f783ac", // pink
  "#4dabf7", // blue
  "#69db7c", // green
];

/** Wavy underline for squiggly markup — one 6×3 pt repeat, colour baked
 *  into the data URI (CSS backgrounds can't take currentColor). */
export function wavyBg(color: string): string {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="6" height="3" viewBox="0 0 6 3">` +
    `<path d="M0 2.2 Q1.5 0 3 2.2 T6 2.2" fill="none" stroke="${color}" stroke-width="1.1"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export const INSPECT_COLORS = {
  block: "#4263eb",
  line: "#2f9e44",
  image: "#f08c00",
  widget: "#9c36b5",
  annot: "#e03131",
  fill: "#0ca678",
  gradient: "#e8590c",
} as const;
